import { FC, useEffect, useState } from 'react';
import { Nameable } from '../services/userManager';
import { Autocomplete, Box, TextField, styled } from '@mui/material';
import { ModalHeader } from '../modals/ModalHeader';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import logger from '../services/loggerManager';
import { MuiTrackerButton } from './TrackerButtonWrapper';
import { ButtonName } from '../constants';
import { addToContext } from '../services/contextManager';
import { useUpdateConfigurationHandler } from '../services/lendersConfigurationManager';
import { useResetCurrentModal } from '../services/modalManager';
import { useApplyStartOver } from '../services/flowManager';
import {
  useGetStoreId,
  useDefaultStore,
  useGetStores,
} from '../services/storeManager';

const Container = styled(Box)(({ theme }) => ({
  width: theme.spacing(75),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
}));

const AutoCompleteWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 12),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    ...theme.typography.body2,
  },
}));

type StoreSelectorProps = {
  updateClose: (func: () => void) => void;
  close: () => void;
};

const StoreSelector: FC<StoreSelectorProps> = ({ updateClose, close }) => {
  const storeId = useGetStoreId();
  const [store, updateStore] = useDefaultStore();
  const stores = useGetStores();
  const [newStore, setNewStore] = useState(store);
  const resetCurrentModal = useResetCurrentModal();
  const updateConfigurationHandler = useUpdateConfigurationHandler();
  const applyStartOver = useApplyStartOver();
  useEffect(() => {
    updateClose(() => () => {
      resetCurrentModal();
      logger.log(`Closed store selector modal`, {
        filename: 'StoreSelector.tsx',
        function: 'closeModal',
      });
    });
  }, []);

  const handleStoreSelection = (
    _: React.SyntheticEvent<Element, Event>,
    store: Nameable | null,
  ) => {
    if (store?.id !== storeId) {
      setNewStore(store ?? { name: '', id: '' });
    }
  };

  const handleSaveSelectedStore = () => {
    addToContext('storeId', newStore.id);
    updateStore(newStore);
    close();
    applyStartOver();
    updateConfigurationHandler(newStore?.id === store.id);
  };
  if (!stores) return null;
  return (
    <Container>
      <ModalHeader title="Switch store" />
      <AutoCompleteWrapper>
        <Autocomplete
          onChange={handleStoreSelection}
          getOptionLabel={(o) => o.name}
          options={stores}
          defaultValue={stores.find((s) => s.id === storeId)}
          renderInput={(params) => (
            <StyledTextField
              label="Select Store"
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon />,
              }}
            />
          )}
        />
      </AutoCompleteWrapper>
      <MuiTrackerButton
        buttonName={ButtonName.SaveStore}
        disabled={!newStore.id || newStore.id === storeId}
        variant="contained"
        fullWidth
        onClick={handleSaveSelectedStore}
        startIcon={<StoreIcon />}
      >
        Save changes
      </MuiTrackerButton>
    </Container>
  );
};

export { StoreSelector };
